import { SxProps, Theme } from "@mui/material";

export const rootStyles: SxProps<Theme> = (theme: Theme) => ({
  width: "100%",
  height: "100%",
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, #0B0F16 0%, #05070B 100%)"
      : `linear-gradient(180deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[50]} 100%)`,
  color: theme.palette.text.primary,
  position: "relative",
  overflow: "hidden",
});

export const layoutStyles: SxProps<Theme> = (theme: Theme) => ({
  height: "100%",
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: "320px 1fr",
  gridTemplateRows: "auto 1fr",
  gap: 2,
  padding: 2,
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto 1fr",
    gap: { xs: 0.75, sm: 1 },
    padding: { xs: 0.75, sm: 1 },
    "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
      {
        padding: "4px",
        gap: "4px",
      },
    // Rotate on phones (including iPhone 14 Pro Max: 932×430) — use max-height, not max-width
    "@media (orientation: landscape) and (max-height: 500px)": {
      height: "100%",
      maxHeight: "100%",
    },
  },
});

export const topBarStyles: SxProps<Theme> = (theme: Theme) => ({
  gridColumn: "1 / -1",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 14px",
  borderRadius: 2,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 2px rgba(15, 23, 42, 0.04)",
  backdropFilter: theme.palette.mode === "dark" ? "blur(10px)" : "none",
  [theme.breakpoints.down("md")]: {
    gridColumn: "auto",
    padding: { xs: "6px 10px", sm: "8px 12px" },
    "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
      {
        padding: "4px 6px",
      },
  },
});

export const leftPanelStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  height: "100%",
  minHeight: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
});

/** Theme callback (not SxProps) so it can be composed inside `sx={[...]}` arrays. */
export const cardStyles = (theme: Theme) => ({
  borderRadius: 2,
  padding: 2,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
});

/** Theme callback (not SxProps) so it can be composed inside `sx={[...]}` arrays. */
export const stagePanelStyles = (theme: Theme) => ({
  borderRadius: 2,
  padding: 2,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minHeight: 0,
});

export const micButtonStyles: SxProps = {
  width: { xs: 56, sm: 74, md: 74, lg: 76 },
  height: { xs: 56, sm: 74, md: 74, lg: 76 },
  borderRadius: "999px",
  backgroundColor: "rgba(46, 204, 113, 0.18)",
  border: "1px solid rgba(46, 204, 113, 0.35)",
  color: "rgba(46, 204, 113, 1)",
  "&:hover": {
    backgroundColor: "rgba(46, 204, 113, 0.24)",
  },
};

export const micButtonMutedStyles: SxProps = {
  width: { xs: 56, sm: 74, md: 74, lg: 76 },
  height: { xs: 56, sm: 74, md: 74, lg: 76 },
  borderRadius: "999px",
  backgroundColor: "rgba(231, 76, 60, 0.18)",
  border: "1px solid rgba(231, 76, 60, 0.35)",
  color: "rgba(231, 76, 60, 1)",
  "&:hover": {
    backgroundColor: "rgba(231, 76, 60, 0.24)",
  },
};

export const getCallMicDimensions = (isSmDown: boolean, isMdDown: boolean) => ({
  buttonPx: isSmDown ? 40 : isMdDown ? 74 : 76,
  iconPx: isSmDown ? 20 : isMdDown ? 34 : 38,
});

export const getRoleplayCallMicDimensions = (
  isSmDown: boolean,
  isMdDown: boolean,
) => ({
  buttonPx: isSmDown ? 34 : isMdDown ? 52 : 60,
  iconPx: isSmDown ? 17 : isMdDown ? 26 : 30,
});

export const getRoleplayMicButtonSx = (
  isActive: boolean,
  buttonPx: number,
): SxProps<Theme> => ({
  width: buttonPx,
  height: buttonPx,
  borderRadius: "999px",
  backgroundColor: isActive ? "#166534" : "#7F1D1D",
  border: "none",
  color: "#FFFFFF",
  touchAction: "none",
  userSelect: "none",
  WebkitUserSelect: "none",
  "&:hover": {
    backgroundColor: isActive ? "#15803D" : "#991B1B",
  },
});

export const roleplayMicFabContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "transparent",
  border: "none",
  p: 0,
};

export const getRoleplayMicFabSx = (
  isActive: boolean,
  buttonPx: number,
  isDisabled = false,
): SxProps<Theme> => ({
  ...getRoleplayMicButtonSx(isActive, buttonPx),
  position: "relative",
  zIndex: 2,
  ...(isActive
    ? {
        boxShadow:
          "0 0 0 4px rgba(34, 197, 94, 0.28), 0 10px 28px rgba(0, 0, 0, 0.42)",
        "&:hover": {
          backgroundColor: "#15803D",
          boxShadow:
            "0 0 0 5px rgba(34, 197, 94, 0.34), 0 12px 30px rgba(0, 0, 0, 0.45)",
        },
      }
    : {}),
  ...(isDisabled
    ? {
        backgroundColor: "rgba(127, 29, 29, 0.4)",
        color: "rgba(255, 255, 255, 0.45)",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "rgba(127, 29, 29, 0.4)",
        },
        "&.Mui-disabled": {
          backgroundColor: "rgba(127, 29, 29, 0.4)",
          color: "rgba(255, 255, 255, 0.45)",
          opacity: 1,
        },
      }
    : {}),
});

export const getRoleplayMicFabPressWrapperSx = (
  isActive: boolean,
  pressScale: number,
): SxProps<Theme> => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.15s ease",
  transform: isActive ? `scale(${pressScale})` : "scale(1)",
  transformOrigin: "center center",
  touchAction: "none",
  zIndex: isActive ? 20 : undefined,
});

export const roleplayControlsRowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-around",
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
};

export const ROLEPLAY_MIC_PRESS_SCALE = 1.333;
// Idle video mic is already 2× base; keep press growth modest so it stays usable.
export const ROLEPLAY_VIDEO_MIC_PRESS_SCALE = 1.25;
// Video roleplay idle mic — keep close to base size (was 2×, too large in docked UI).
export const ROLEPLAY_VIDEO_IDLE_MIC_SCALE = 1.15;
// Halo rings expand to this factor of the (pressed) button size.
export const ROLEPLAY_MIC_HALO_EXPAND = 1.85;

export const getRoleplayMicScalePadding = (
  buttonPx: number,
  pressScale = ROLEPLAY_MIC_PRESS_SCALE,
) => Math.ceil((buttonPx * (pressScale - 1)) / 2);

// Extra space below the mic so expanded button + halo never cover the label.
export const getRoleplayMicLabelClearance = (
  buttonPx: number,
  pressScale = ROLEPLAY_MIC_PRESS_SCALE,
) => {
  const pressedPx = buttonPx * pressScale;
  const haloExtra = Math.ceil((pressedPx * (ROLEPLAY_MIC_HALO_EXPAND - 2)) / 2);
  const scaleExtra = getRoleplayMicScalePadding(buttonPx, pressScale);
  return Math.max(haloExtra, scaleExtra);
};

export const roleplayButtonFrameSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 2,
  p: { xs: 0.25, sm: 0.35 },
};

export const getRoleplayEndCallButtonSx = (
  buttonPx: number,
): SxProps<Theme> => (theme) => ({
  width: buttonPx,
  height: buttonPx,
  borderRadius: "999px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(231, 76, 60, 0.18)"
      : "rgba(220, 38, 38, 0.12)",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(231, 76, 60, 0.35)"
      : "rgba(185, 28, 28, 0.45)"
  }`,
  color: theme.palette.mode === "dark" ? undefined : "#B91C1C",
  flexShrink: 0,
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(231, 76, 60, 0.24)"
        : "rgba(220, 38, 38, 0.18)",
  },
});

export const roleplayPushToTalkLabelSx: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 8, sm: 10 },
  color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
  opacity: 0.9,
  fontWeight: 700,
  whiteSpace: "nowrap",
  userSelect: "none",
  position: "relative",
  zIndex: 0,
  flexShrink: 0,
});

export const getRoleplayPushToTalkLabelSx = (
  buttonPx: number,
  pressScale: number,
): SxProps<Theme> => [
  roleplayPushToTalkLabelSx,
  { mt: `${getRoleplayMicLabelClearance(buttonPx, pressScale)}px` },
];

export const roleplayVideoControlsSx = (
  buttonPx: number,
  pressScale: number,
): SxProps<Theme> => {
  const expandedPx = Math.ceil(buttonPx * pressScale);
  const labelClearance = getRoleplayMicLabelClearance(buttonPx, pressScale);

  return {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: { xs: 2, sm: 3 },
    // Keep mic size; trim vertical chrome now that waves live inside the video.
    minHeight: expandedPx + labelClearance + 12,
    py: 0,
    "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
      {
        minHeight: expandedPx + labelClearance,
        gap: 1.5,
      },
  };
};

// End call sits next to the mic (same control group), not in a screen corner.
export const roleplayVideoEndCallFrameSx: SxProps<Theme> = {
  position: "relative",
  left: "auto",
  bottom: "auto",
  flexShrink: 0,
  alignSelf: "center",
};

export const roleplayVideoMicFrameSx = (
  buttonPx: number,
  pressScale: number,
): SxProps<Theme> => {
  const expandedPx = Math.ceil(buttonPx * pressScale);
  const scalePadding = getRoleplayMicScalePadding(buttonPx, pressScale);
  const labelClearance = getRoleplayMicLabelClearance(buttonPx, pressScale);

  return {
    ...roleplayMicFabContainerSx,
    minWidth: expandedPx + scalePadding * 2 + 16,
    minHeight: expandedPx + scalePadding * 2 + labelClearance + 20,
    justifyContent: "flex-end",
    gap: 0,
    "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
      {
        minHeight: expandedPx + scalePadding * 2 + labelClearance + 8,
      },
  };
};

export const getRoleplayEndCallFrameSx = (
  isVideoLayout: boolean,
): SxProps<Theme> =>
  isVideoLayout
    ? ([roleplayButtonFrameSx, roleplayVideoEndCallFrameSx] as SxProps<Theme>)
    : roleplayButtonFrameSx;

export const getRoleplayEndCallIconButtonSx = (
  buttonPx: number,
  endButtonSx?: SxProps<Theme>,
): SxProps<Theme> =>
  [
    getRoleplayEndCallButtonSx(buttonPx),
    ...(endButtonSx ? [endButtonSx] : []),
  ] as SxProps<Theme>;

export const getRoleplayMicFrameSx = (
  isVideoLayout: boolean,
  buttonPx: number,
  pressScale: number,
): SxProps<Theme> =>
  isVideoLayout
    ? roleplayVideoMicFrameSx(buttonPx, pressScale)
    : roleplayMicFabContainerSx;

export const getRoleplayMicIconButtonSx = (
  isActive: boolean,
  buttonPx: number,
  buttonSx?: SxProps<Theme>,
  isDisabled = false,
): SxProps<Theme> =>
  [
    getRoleplayMicFabSx(isActive, buttonPx, isDisabled),
    ...(buttonSx ? [buttonSx] : []),
  ] as SxProps<Theme>;

export const getRoleplayMicScalePadSx = (
  micScalePadding: number,
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: `${micScalePadding}px`,
  position: "relative",
  zIndex: 1,
  overflow: "visible",
});
